import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ExternalLink, ArrowRight } from "lucide-react";
import { gtmClick } from "@/lib/gtm";
import { SEOHead, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["All", "Services", "Retail", "Trades", "Professional", "eCommerce"];

function LaptopMockup({ src, alt, url }) {
  return (
    <div className="relative mx-auto" style={{ maxWidth: "100%" }}>
      {/* Laptop body */}
      <div className="relative rounded-t-xl border-[3px] border-gray-800 bg-gray-800 pt-[3%] px-[3%]">
        {/* Screen bezel top */}
        <div className="absolute top-[6px] left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-gray-600" />
        {/* Screen */}
        <div className="relative w-full overflow-hidden rounded-t-sm bg-white" style={{ aspectRatio: "16/10" }}>
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors duration-300 flex items-center justify-center group"
              data-testid="laptop-visit-link"
            >
              <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#FFD600] border-2 border-black px-5 py-2.5 rounded-full font-bold text-sm flex items-center gap-2 shadow-lg">
                Visit Site <ExternalLink size={14} />
              </span>
            </a>
          )}
        </div>
      </div>
      {/* Laptop base / hinge */}
      <div className="relative mx-auto bg-gradient-to-b from-gray-700 to-gray-800 rounded-b-xl h-3" style={{ width: "110%", marginLeft: "-5%" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-1 bg-gray-600 rounded-b-lg" />
      </div>
    </div>
  );
}

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    axios.get(`${API}/portfolio`).then(r => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = filter === "All" ? items : items.filter(i => i.category === filter);

  return (
    <main className="pt-16" data-testid="portfolio-page">
      <SEOHead
        title="Our Portfolio — Websites We've Built"
        description="See real websites built by No Fuss Websites for Australian businesses. Professional web design across trades, retail, eCommerce, and professional services. Melbourne-based web agency."
        path="/portfolio"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Portfolio", path: "/portfolio" }])}
      />
      <Breadcrumbs items={[{ name: "Portfolio", path: "/portfolio" }]} />
      {/* Hero */}
      <section className="bg-black text-white border-b-4 border-[#FFD600] section-padding">
        <div className="container-main text-center">
          <p className="text-sm font-black uppercase tracking-widest text-[#FFD600] mb-4">Our Work</p>
          <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Real Websites.<br />Real Results.
          </h1>
          <p className="text-xl text-gray-300 font-medium max-w-xl mx-auto">
            Aussie businesses trust us to build their online presence. Browse our latest projects below.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 bg-[#F4F4F5] border-b-2 border-black sticky top-16 z-20" data-testid="portfolio-filters">
        <div className="container-main flex flex-wrap gap-3 justify-center">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              onClick={() => { setFilter(cat); gtmClick(`portfolio_filter_${cat}`, "filter"); }}
              className={`px-5 py-2.5 rounded-full border-2 border-black font-bold text-sm transition-all hover:-translate-y-0.5 ${
                filter === cat ? "bg-[#FFD600] text-black" : "bg-white text-black"
              }`}
              style={{ boxShadow: filter === cat ? "3px 3px 0px 0px rgba(0,0,0,1)" : "" }}
              data-testid={`filter-${cat.toLowerCase().replace(/ /g, "-")}`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Grid */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="portfolio-grid">
        <div className="container-main">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              <p className="text-2xl font-bold">No projects found</p>
              <p className="mt-2">Try a different category filter</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              {filtered.map((item, i) => (
                <div
                  key={item.id}
                  className="group bg-white border-2 border-black rounded-2xl overflow-hidden transition-transform hover:-translate-y-2 duration-200 p-6"
                  style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
                  data-testid={`portfolio-card-${i}`}
                >
                  <LaptopMockup
                    src={item.image_url}
                    alt={item.title}
                    url={item.client_url}
                  />
                  <div className="mt-5">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="bg-[#FFD600] border-2 border-black text-xs font-black px-3 py-1 rounded-full">
                        {item.category}
                      </span>
                      {item.client_url && (
                        <a
                          href={item.client_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-500 hover:text-black font-medium flex items-center gap-1 transition-colors"
                          data-testid={`portfolio-link-${i}`}
                        >
                          {item.client_url.replace(/^https?:\/\//, '').replace(/\/$/, '')} <ExternalLink size={10} />
                        </a>
                      )}
                    </div>
                    <h3 className="text-xl font-black mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>{item.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#FFD600] border-t-4 border-black" data-testid="portfolio-cta">
        <div className="container-main text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            Want a Website Like These?
          </h2>
          <p className="text-xl font-semibold mb-8 max-w-xl mx-auto">
            Your website could be in our portfolio. Starting at just $499.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/purchase" className="btn-secondary inline-flex items-center gap-2 text-lg" data-testid="portfolio-cta-btn" onClick={() => gtmClick("portfolio_get_my_website")}>
              Get My Website <ArrowRight size={18} />
            </Link>
            <Link to="/packages" className="bg-white border-2 border-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
              View Packages & Pricing <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="section-padding bg-white border-t-2 border-black">
        <div className="container-main">
          <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>Related Pages</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "How We Build Websites", desc: "Learn our simple 4-step process from brief to launch.", link: "/how-it-works" },
              { title: "Frequently Asked Questions", desc: "Everything you need to know about getting a website.", link: "/faq" },
              { title: "About Our Team", desc: "Meet the people behind No Fuss Websites.", link: "/about" },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="border-2 border-black rounded-xl p-5 hover:-translate-y-1 transition-all bg-white" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                <h3 className="font-black mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
